import { ExclamationCircleFilled } from '@ant-design/icons';
import { Button, Row } from 'antd';
import { TableRowSelection } from 'antd/lib/table/interface';
import { useGetTaskListQuery } from 'app/api';
import { StyledTable } from 'app/components/StyledTable/StyledTable';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { ITasksArrResponseItem } from 'app/containers/Task/slice/types';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { TextRegular24 } from 'styles/typography';
import { getMatchedTasksWithRouteTableColumns } from '../AddRouteToTaskDrawer/AddRouteToTaskDrawer.helpers';

export const EmptyStateWrapper = styled.div`
  padding: 50px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const EmptyDescription = styled.div`
  margin: 8px auto 32px;
  color: #595959;
  font-size: 12px;
  line-height: 22px;
  max-width: 347px;
  text-align: center;
`;

interface IProps {
  isImplementSelected: boolean;
  startDate: string | null;
  endDate: string | null;
  taskCategoryIds: number[];

  selectedTaskId: number | null;
  setSelectedTaskId: React.Dispatch<React.SetStateAction<number | null>>;
  onCreateNewTask: () => void;
}

const TasksByRoute: FC<IProps> = ({
  isImplementSelected,
  startDate,
  endDate,
  taskCategoryIds,
  selectedTaskId,
  setSelectedTaskId,
  onCreateNewTask,
}) => {
  const { t } = useTranslation();

  const envId = useSelector(selectSelectedEnvId) as number;
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const companyId = useSelector(selectSelectedCompanyId);

  const {
    data: taskResponse,
    isFetching: isTasksLoading,
  } = useGetTaskListQuery(
    {
      envId,
      companyGroupId,
      companyId,
      taskCategoryIds,
      startDate,
      endDate,
    },
    { skip: !isImplementSelected },
  );

  const tasks = taskResponse?.content;

  useEffect(() => {
    if (!isImplementSelected && selectedTaskId) {
      setSelectedTaskId(null);
    }
    if (!selectedTaskId || !tasks) {
      return;
    }

    const isSelectedTaskIdInTable = tasks.find(
      task => task.id === selectedTaskId,
    );
    if (!isSelectedTaskIdInTable && selectedTaskId) {
      setSelectedTaskId(null);
    }
  }, [tasks, selectedTaskId, setSelectedTaskId, isImplementSelected]);

  const rowSelection: TableRowSelection<ITasksArrResponseItem> = {
    type: 'radio',
    selectedRowKeys: selectedTaskId ? [selectedTaskId] : undefined,

    onChange: (
      selectedRowKeys: React.Key[],
      selectedRows: ITasksArrResponseItem[],
    ) => {
      setSelectedTaskId((selectedRowKeys[0] as number) || null);
    },
  };

  return (
    <>
      {isImplementSelected ? (
        <>
          {!tasks?.length && !isTasksLoading ? (
            <EmptyStateWrapper>
              <ExclamationCircleFilled
                style={{ color: '#FA541C', fontSize: 63, marginBottom: 28 }}
              />
              <TextRegular24>
                {t('Task.NoTasksFound', 'No tasks found')}
              </TextRegular24>
              <EmptyDescription>
                {t(
                  'Task.NoTasksFoundDescription',
                  'We did not find any open tasks according to the period of the routes, the type of implement and the category of the task it performs.',
                )}
              </EmptyDescription>

              <Button type="primary" onClick={onCreateNewTask}>
                {t('Task.CreateNewTask', 'Create a new task')}
              </Button>
            </EmptyStateWrapper>
          ) : (
            <>
              <Row style={{ marginBottom: 12, marginTop: 24 }}>
                {t('Task.ActiveTasksFound', 'Active tasks found')}:{' '}
                {tasks?.length || 0}
              </Row>
              <StyledTable
                loading={isTasksLoading}
                rowSelection={rowSelection}
                rowKey={task => task.id}
                dataSource={tasks}
                columns={getMatchedTasksWithRouteTableColumns()}
                pagination={false}
              />
              <Row align="middle" justify="end" style={{ marginTop: 13 }}>
                <Button type="primary" onClick={onCreateNewTask}>
                  {t('Task.CreateNewTask', 'Create a new task')}
                </Button>
              </Row>
            </>
          )}
        </>
      ) : (
        <Row align="middle" justify="center" style={{ height: 200 }}>
          {t('Task.SelectImplements', 'Select implements')}
        </Row>
      )}
    </>
  );
};

export default TasksByRoute;
